import { isEmpty, isNil } from 'lodash';
import { ONE_HUNDRED_VALUE } from 'common/config/app';
import {
  FUND_INVESTMENTS_WEIGHTING_VALIDATIONS,
  FUND_INVESTMENTS_WEIGHTINGS_GREATER_THAN_100,
  FUND_INVESTMENTS_WEIGHTINGS_LOWER_THAN_100,
} from 'pages/FundInvestments/common/constants';
import { clearAndAddFeedback } from 'pages/ValuationsAllocation/util';
import { getNumberValue, getObjectValue } from 'utilities';
import { ValidateComparisonWeightingCellsParams } from './validateComparisonWeightingCells.types';

const validateComparisonWeightingCells = (params: ValidateComparisonWeightingCellsParams): number | null => {
  const { addFeedback, parsedCells, removeFeedback, removeFeedbackFromCell } = params;

  // Gather all cells representing a weighting for the current comparison group
  const comparisonWeightingCells = Object.values(getObjectValue(parsedCells)).filter(parsedCell => {
    const { data } = getObjectValue(parsedCell);
    const { capIqId, isWeighting = false } = getObjectValue(data);
    return !isNil(capIqId) && isWeighting;
  });

  // If there are no comparison weighting cells, exit early
  if (isEmpty(comparisonWeightingCells)) {
    return null;
  }

  // Extract the weighting value from each cell and convert to a percentage
  const comparisonWeightings = comparisonWeightingCells.map(
    cell => getNumberValue(cell?.value as number) * ONE_HUNDRED_VALUE
  );

  // Calculate the total of all comparison weightings
  const totalComparisonWeightings = comparisonWeightings.reduce(
    (accumulator, current) => accumulator + getNumberValue(current),
    0
  );

  if (totalComparisonWeightings < ONE_HUNDRED_VALUE - Number.EPSILON) {
    // Add feedback if the total is less than 100%
    clearAndAddFeedback({
      addFeedback,
      feedback: FUND_INVESTMENTS_WEIGHTINGS_LOWER_THAN_100,
      removeFeedback,
      validations: FUND_INVESTMENTS_WEIGHTING_VALIDATIONS,
    });
  } else if (totalComparisonWeightings > ONE_HUNDRED_VALUE + Number.EPSILON) {
    // Add feedback if the total is greater than 100%
    clearAndAddFeedback({
      addFeedback,
      feedback: FUND_INVESTMENTS_WEIGHTINGS_GREATER_THAN_100,
      removeFeedback,
      validations: FUND_INVESTMENTS_WEIGHTING_VALIDATIONS,
    });
  } else {
    // Remove feedback if the total is exactly 100%
    comparisonWeightingCells.forEach(cell => {
      removeFeedbackFromCell?.(cell, FUND_INVESTMENTS_WEIGHTINGS_LOWER_THAN_100);
      removeFeedbackFromCell?.(cell, FUND_INVESTMENTS_WEIGHTINGS_GREATER_THAN_100);
    });
  }

  return totalComparisonWeightings;
};

export default validateComparisonWeightingCells;
